import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Subject } from 'rxjs';
import { PaginatedList } from 'src/app/shared/models';
import {
  GET_INSPECTION_TASKS_BY_ID,
  GET_INSPECTION_CONFIGURATIONS_BY_FACILITY_ID,
  GET_INSPECTION_CONFIGURATIONS_BY_FACILITY,
  UPLOAD_TASKS_CSV,
  CONFIGURE_INSPECTION,
  GET_ALL_TASKS_BY_KIND,
  GET_ALL_USER_ZONES,
  GET_USER_ZONES_BY_ID,
  UPDATE_USER_ZONE_ASSIGNMENTS,
  GET_ZONES_BY_FACILITY,
} from '../queries/qbuilt.queries';
import { Observable } from 'rxjs';
import { ChecklistValues } from '../models/checklist.model';
import { ItemListPanelConstants } from 'src/app/shared/models/constants/item-list-panel.constants';
import { MutationResult, UpdateResult } from 'src/app/shared/graph-queries/types';
import { map } from 'rxjs/operators';
import { TasksByKind } from '../models/TasksByKind';

@Injectable({
  providedIn: 'root',
})
export class QBuiltService {
  private _secondFormData = new BehaviorSubject<any>(null);
  secondFormData$ = this._secondFormData.asObservable();

  private _allRequiredItems = new BehaviorSubject<boolean>(undefined);
  allRequiredItems$ = this._allRequiredItems.asObservable();

  public tasksByKindData = new Subject<any>();
  tasksByKindData$ = this.tasksByKindData.asObservable();

  public inspectionConfiguration = new Subject<any>();
  inspectionConfiguration$ = this.inspectionConfiguration.asObservable();

  public inspectionConfigurationList = new Subject<any>();
  inspectionConfigurationList$ =
    this.inspectionConfigurationList.asObservable();

  private _allRequiredTasks = new BehaviorSubject<any[]>([]);
  allRequiredTasks$ = this._allRequiredTasks.asObservable();

  private _allOptionalTasks = new BehaviorSubject<any[]>([]);
  allAOCTasks$ = this._allOptionalTasks.asObservable();

  private currentItemList = new BehaviorSubject<any[]>([]);
  currentItemList$ = this.currentItemList.asObservable();

  private itemInUseList = new BehaviorSubject<any[]>([]);
  itemInUseList$ = this.itemInUseList.asObservable();

  private facilityNumber = new BehaviorSubject<string>(null);
  facilityNumber$ = this.facilityNumber.asObservable();

  private checklistTableData = new BehaviorSubject<any[]>([]);
  checklistTableData$ = this.checklistTableData.asObservable();

  constructor(
    private apolloClient: Apollo,
    public itemConstants: ItemListPanelConstants
  ) {}

  updateSecondFormData(data: any) {
    this._secondFormData.next(data);
  }

  resetSecondFormData() {
    this._secondFormData.next(null);
  }

  getSecondFormData() {
    return this._secondFormData.getValue();
  }

  setAllRequiredItemsStatus(status: boolean) {
    this._allRequiredItems.next(status);
  }

  getAllRequiredItemsStatus() {
    return this.allRequiredItems$;
  }

  //List of task ids that are in use to update styling
  updateItemInUseList(items: any[]) {
    this.itemInUseList.next(items);
  }

  getItemInUseList(): Observable<any[]> {
    return this.itemInUseList.asObservable();
  }

  //keeps track of full list of just required tasks formatted for front end
  setAllRequiredTasks(tasks: any[]) {
    this._allRequiredTasks.next(tasks);
  }

  getAllRequiredTasks(): Observable<any[]> {
    return this._allRequiredTasks.asObservable();
  }

  //keeps track of full list of just optional tasks formatted for front end
  setOptionalTasks(tasks: any[]) {
    this._allOptionalTasks.next(tasks);
  }  
  
  getAllOptionalTasks(): Observable<any[]> {
    return this._allOptionalTasks.asObservable();
  }


  //keeps list of current item list in panel to keep in case of refresh
  setCurrentItemList(items: any[]) {
    this.currentItemList.next(items);
  }

  getCurrentItemList(): Observable<any[]> {
    return this.currentItemList.asObservable();
  }

  setAllTasks(tasks: any[]) {
    this._allRequiredTasks.next(tasks);
  }

  getAllTasks(): Observable<any[]> {
    return this._allRequiredTasks.asObservable();
  }

  getOptionalTasks(tasks: any[]) {
  const optionalOnly = tasks.filter(t => t.RequiredFlag === false);

  const optionalTasks = optionalOnly.map(task => ({
    taskId: task.InspectionTaskId,
    taskType: this.itemConstants.optionalItem,
    taskName: task.InspectionTaskName,
    taskDepartments: task.InspectionTaskDepartments,
    classificationAssignments: task.ClassificationAssignments
  }));

  this.setOptionalTasks(optionalTasks);
  return optionalTasks;
}

  setFacilityNumber(key: any) {
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const temp = JSON.parse(raw);
    if (!temp) return;
    const number = temp.map(
      (i: { Facility: { KeyChain: { LegacyId: any } } }) =>
        i.Facility.KeyChain.LegacyId
    )[0];
    this.facilityNumber.next(number);
  }

  GetAllTasksByKinds(filter?: any): void {
    this.apolloClient
      .query({
        query: GET_ALL_TASKS_BY_KIND,
        variables: {
          filter: JSON.stringify(filter),
        },
      })
      .subscribe((response: any) => {
        let taskList = response.data.GetAllTasksByKinds;
        let tasks = new PaginatedList<TasksByKind>(taskList);
        this.tasksByKindData.next(tasks);
        return tasks.Items;
      });
  }

  getInspectionTasksById(id: string): void {
    this.apolloClient
      .query({
        query: GET_INSPECTION_TASKS_BY_ID,
        variables: { id },
        fetchPolicy: 'no-cache',
      })
      .subscribe((response: any) => {
        this.inspectionConfiguration.next(response.data.GetInspectionTasksById);
      });
  }

  getInspectionConfigurationsByFacilityId(userFacility: string): void {
    this.apolloClient
      .query({
        query: GET_INSPECTION_CONFIGURATIONS_BY_FACILITY_ID,
        variables: { userFacility },
        fetchPolicy: 'no-cache',
      })
      .subscribe((response: any) => {
        this.inspectionConfigurationList.next(
          response.data.GetInspectionConfigurationsByFacilityId
        );
      });
  }

  uploadTasks(csvString: string): Observable<UpdateResult> {
    return this.apolloClient
      .mutate<UpdateResult>({
        mutation: UPLOAD_TASKS_CSV,
        variables: {
          payload: csvString,
        },
      })
      .pipe(
        map((result: MutationResult) => {
          return result.data.UploadTasks as UpdateResult;
        })
      );
  }

  saveChecklist(checklistData: ChecklistValues): Observable<UpdateResult> {
    return this.apolloClient
      .mutate<UpdateResult>({
        mutation: CONFIGURE_INSPECTION,
        variables: {
          payload: JSON.stringify(checklistData),
        },
      })
      .pipe(
        map((result: MutationResult) => {
          return result.data.ConfigureInspection as UpdateResult;
        })
      );
  }
  getInspectionConfigurationsByFacility(userFacility: string): void {
    this.apolloClient
      .query({
        query: GET_INSPECTION_CONFIGURATIONS_BY_FACILITY,
        variables: { userFacility },
        fetchPolicy: 'no-cache',
      })
      .subscribe((response: any) => {
        this.checklistTableData.next(
          response.data.GetInspectionConfigurationsByFacility
        );
      });
  }

  getAllUserZones(plantNumber: string): Observable<any[]> {
    return this.apolloClient
      .query<any>({
        query: GET_ALL_USER_ZONES,
        variables: { userFacility: plantNumber },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((response) => response.data.GetAllUserZones));
  }

  getUserZonesById(userId: number, facilityNumber: string): Observable<any> {
    return this.apolloClient
      .query<any>({
        query: GET_USER_ZONES_BY_ID,
        variables: { id: String(userId), userFacility: facilityNumber },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((response) => response.data.GetUserZonesById));
  }

  getZonesByFacility(facilityNumber: string): Observable<any[]> {
    return this.apolloClient
      .query<any>({
        query: GET_ZONES_BY_FACILITY,
        variables: { userFacility: facilityNumber },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((response) => response.data.GetZoneConfigurationsByFacility));
  }

  updateUserZoneAssignments(userId: number, zoneIds: number[], zoneIdsToRemove: number[]): Observable<UpdateResult> {
    return this.apolloClient
      .mutate<any>({
        mutation: UPDATE_USER_ZONE_ASSIGNMENTS,
        variables: {
          payload: JSON.stringify({ UserId: userId, ZoneIds: zoneIds, ZoneIdsToRemove: zoneIdsToRemove }),
        },
      })
      .pipe(
        map((result: MutationResult) => result.data.UpdateUserZoneAssignments as UpdateResult)
      );
  }
}
